/** The quote's service row (moved from `quote/quote-rows.tsx` at step 8). */
import { type ReactNode } from 'react'
import { type QuoteLineKind, type QuoteServiceView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { AddRow, type MenuAt } from './add-row.js'
import { MarginCell } from './grid.js'
import { ServiceShell } from './service-shell.js'
import { formatNumber, formatRate } from '../../lib/format.js'
import { OPTIONAL_LABEL } from '../copy.js'
import { MeasureChip } from './measure-chip.js'
import { VisitTag } from './visit-card.js'
import { ADD_LINK_LABELS } from './line-row.js'

// ---------------------------------------------------------------------------
// The service
// ---------------------------------------------------------------------------

export interface ServiceRowProps {
  service: QuoteServiceView
  children: ReactNode
  collapsed: boolean
  onToggle: () => void
  onEditName: () => void
  onEditMeasure: () => void
  /** The Measure popover is open off this chip — the accent ring (cb-19 · 1). */
  measureOpen?: boolean
  /**
   * The measure lives on the package group's header instead.
   *
   * A chip here would be a second control for one number, and the one that
   * cannot actually change it: a package's services scale from the group.
   */
  fromPackage?: boolean
  /** Given the link itself, so the picker hangs off the row it was asked for. */
  onAdd: (kind: QuoteLineKind, anchor: HTMLElement) => void
  /** The ⋮'s point and the ⋮ itself (`MenuAt`). */
  onMenu: (at: MenuAt) => void
  /**
   * A quote that is not a draft (walkthrough findings §4, folder 52): the
   * name and the measure open nothing, the add links and the ⋮ are absent.
   */
  readOnly?: boolean
}

/**
 * A quote's service, on the shared shell.
 *
 * What this adds is the measure chip — the one number that drives every line
 * below it, so it is a control rather than a label — and the hours and money.
 * The rung itself, its circle and its collapse are `ServiceShell`.
 */
export function ServiceRow({
  service, children, collapsed,
  onToggle, onEditName, onEditMeasure, onAdd, onMenu, fromPackage, measureOpen, readOnly = false,
}: ServiceRowProps) {
  /*
   * A package's service is measured on the package's header, not here; and a
   * service with No measure has nothing to look up — its lines are plain
   * quantities (§6.7 item 2).
   */
  const measured = service.serviceId !== null && !fromPackage && service.measurementTypeId !== null
  return (
    <ServiceShell
      name={service.name}
      onEditName={readOnly ? undefined : onEditName}
      tags={(
        <>
          <DivisionTag name={service.divisionName} code={service.divisionCode} />
          {service.optional && <VisitTag tone="info">{OPTIONAL_LABEL}</VisitTag>}
        </>
      )}
      /*
       * The service's numbers stand in the same columns as its lines' (Aaron:
       * "These things need to align"). The measure spans the quantity and
       * unit columns, because it *is* the quantity the lines below were
       * worked out from; then the hours, the cost and the total, each over
       * the matching figure on the lines.
       *
       * The measure used to sit after the name, where a long name and a
       * division tag squeezed it to "pac…", and the hours and cost shared the
       * 110px unit column and ran into the next one.
       */
      /*
       * Every column totalled, under the header that names it (folder 44
       * §5.2): the measure spans Qty and Unit because it *is* the quantity
       * the lines were worked out from; the total sits under Price; ours on
       * the band. The hours moved beside the name, where they read as a fact
       * about the service rather than a column of their own.
       */
      sub={service.hours > 0
        ? (
          <span className="tnum whitespace-nowrap text-[11.5px] text-text-muted">
            {formatNumber(Math.round(service.hours * 10) / 10)} h
          </span>
        )
        : null}
      columns={{
        measure: measured
          ? (
            /*
             * The measure chip (cb-19 · 1), the same one a package's header
             * wears. It opens the Measure popover, where the number is
             * looked up, not typed.
             */
            <MeasureChip
              measure={service.measure}
              unit={service.measureUnit}
              measurementName={service.measurementName}
              open={measureOpen}
              onClick={onEditMeasure}
              disabled={readOnly}
            />
          )
          : undefined,
        price: (
          <b className="tnum text-right text-[14px] font-bold text-text">{formatRate(service.total)}</b>
        ),
        cost: (
          <span className="tnum whitespace-nowrap text-right text-[13px] text-text-muted">
            {formatRate(service.cost)}
          </span>
        ),
        margin: <MarginCell pct={service.marginPct} />,
      }}
      collapsed={collapsed}
      onToggle={onToggle}
      onMenu={readOnly ? undefined : onMenu}
      addRow={readOnly ? undefined : (
        <AddRow
          /*
           * Four, not five. A sub is labor with somebody else's name on the
           * invoice, and a link for it taught nobody that the quote could
           * carry one — it just made the row longer.
           */
          /*
           * Plural, and the same four words the service recipe's links use.
           * A link adds one of many; the row's own label stays singular, and
           * `QUOTE_LINE_KIND_LABELS` is still where that comes from, so the
           * two cannot come apart.
           */
          kinds={(['item', 'labor', 'equipment', 'deployment'] as const).map((kind) => ({
            key: kind, label: ADD_LINK_LABELS[kind],
          }))}
          onAdd={(kind, at) => onAdd(kind as QuoteLineKind, at)}
        />
      )}
    >
      {children}
    </ServiceShell>
  )
}

/** Exported for the service picker's rows (item 3.3), so a pick wears the tag it will carry. */
export function DivisionTag({ name, code }: { name: string; code: string }) {
  /*
   * A division's colour comes from the service-line set in §3.1 when its code
   * names one, and falls back to the quiet tag otherwise. A tenant may invent
   * "Holiday lighting", and inventing a colour for it here would be a colour
   * nothing else in the product agrees with.
   */
  const group = DIVISION_TONES[code.toLowerCase()] ?? null
  /*
   * The tag gives way before the name does. "Annual colour r… Enhancements &
   * installs" said the less useful half in full; the service is what the row
   * is, and the division is a colour somebody recognises from three letters.
   */
  return (
    <span
      title={name}
      className={cn(
        'inline-flex h-[19px] min-w-[3rem] shrink-[4] items-center whitespace-nowrap rounded-tag px-1.5',
        'text-[11px] font-semibold',
        !group && 'bg-line-soft text-text-muted',
      )}
      style={group
        ? {
          background: `rgb(var(--status-serviceLine-${group}-bg))`,
          color: `rgb(var(--status-serviceLine-${group}-fg))`,
        }
        : undefined}
    >
      <span className="min-w-0 truncate">{name}</span>
    </span>
  )
}

const DIVISION_TONES: Record<string, string> = {
  maint: 'maintenance', maintenance: 'maintenance',
  enh: 'enhancements', enhancements: 'enhancements', hard: 'enhancements',
  irr: 'irrigation', irrigation: 'irrigation',
  snow: 'snow',
}
